import { useEffect, useState } from 'react';
import type { Alerta, SeveridadAlerta } from '@matias-asset-os/contracts';
import { t, type Idioma } from '@matias-asset-os/i18n';
import { api } from '../api.js';

interface Props {
  idioma: Idioma;
  onVolver: () => void;
  onAbrirPropiedad: (id: string) => void;
  /** Para que la insignia del menu se entere sin esperar al siguiente refresco. */
  onLeidas?: () => void;
}

const CLASE_SEVERIDAD: Record<SeveridadAlerta, string> = {
  ALTA: 'rojo',
  MEDIA: 'ambar',
  BAJA: 'neutra',
};

/**
 * Alertas: lo que reclama atención ahora. No es un buzón que haya que vaciar,
 * sino una vista derivada del estado real —incidencias abiertas y propiedades
 * sin activar—: cuando el problema se resuelve, la alerta desaparece sola.
 */
export function Notificaciones({ idioma, onVolver, onAbrirPropiedad, onLeidas }: Props) {
  const [alertas, setAlertas] = useState<Alerta[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [marcando, setMarcando] = useState(false);

  useEffect(() => {
    api
      .alertas()
      .then((r) => setAlertas(r.alertas))
      .catch(() => setError(t(idioma, 'error.servidor')));
  }, [idioma]);

  const sinLeer = (alertas ?? []).filter((a) => !a.leida).length;

  /*
    Dar por vistas no cierra nada: la incidencia sigue abierta y la alerta sigue
    en la lista. Lo unico que se apaga es la insignia, y solo para quien pulsa.
    Si manana aparece una nueva, vuelve —que es para lo que sirve—.
  */
  const marcarLeidas = () => {
    setMarcando(true);
    api
      .marcarAlertasLeidas()
      .then(() => api.alertas())
      .then((r) => {
        setAlertas(r.alertas);
        onLeidas?.();
      })
      .catch(() => setError(t(idioma, 'error.servidor')))
      .finally(() => setMarcando(false));
  };

  const contexto = (a: Alerta) =>
    a.tipo === 'INCIDENCIA'
      ? `${t(idioma, 'alertas.incidencia')} · ${a.propiedadCodigo ?? ''} · ${a.detalle}`
      : `${t(idioma, 'alertas.activacion')} · ${a.propiedadCodigo ?? ''} · ${t(idioma, 'alertas.requisitos', { total: a.detalle })}`;

  return (
    <main>
      <div className="cabecera-seccion">
        <div>
          <h1>{t(idioma, 'alertas.titulo')}</h1>
          <p>
            {t(idioma, 'alertas.resumen', { total: alertas?.length ?? 0 })}
            {sinLeer > 0 && ` · ${t(idioma, 'alertas.sinLeer', { total: sinLeer })}`}
          </p>
        </div>
        <div className="acciones">
          {sinLeer > 0 && (
            <button type="button" onClick={marcarLeidas} disabled={marcando}>
              {t(idioma, 'alertas.marcarLeidas')}
            </button>
          )}
          <button type="button" className="secundario" onClick={onVolver}>
            {t(idioma, 'app.volver')}
          </button>
        </div>
      </div>

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      {alertas === null && !error && <p>{t(idioma, 'app.cargando')}</p>}

      {alertas !== null && alertas.length === 0 && (
        <div className="tarjeta">
          <p>{t(idioma, 'alertas.vacio')}</p>
        </div>
      )}

      {alertas !== null && alertas.length > 0 && (
        <div className="tarjeta plana">
          <div className="lista">
            {alertas.map((a) => {
              const fila = (
                <>
                  <span className={`pastilla ${CLASE_SEVERIDAD[a.severidad]}`}>
                    {t(idioma, `alertaSeveridad.${a.severidad}`)}
                  </span>
                  <span className="crecer">
                    <b>{a.titulo}</b>
                    <small>{contexto(a)}</small>
                  </span>
                  {a.propiedadId && (
                    <span aria-hidden="true" className="suave">
                      ›
                    </span>
                  )}
                </>
              );
              return a.propiedadId ? (
                <button
                  key={a.id}
                  type="button"
                  className={`fila fila-pulsable${a.leida ? ' alerta-leida' : ''}`}
                  onClick={() => onAbrirPropiedad(a.propiedadId!)}
                >
                  {fila}
                </button>
              ) : (
                <div key={a.id} className="fila">
                  {fila}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </main>
  );
}
